/**
 * Which trip day a small-hours time belongs to — the ONE late-night rule
 * (Kurtis 2026-10-24, docs/plans/ADD_NOW_PLAN.md "Late night"). From 06:00 a
 * time is on its calendar day. Before 06:00 it is either the end of the night
 * before (stored as an exact time with the evening block — the add sheet's
 * "Late night", utils/planWhen) or the new day's early morning.
 *
 * - Live (`tripNow`: the map's "today", "I'm here"): the night before, unless
 *   the new day already has something starting within 2 hours — a 5 AM taxi,
 *   a sunrise hike. Out and tapping at 2 AM, the night is still going.
 * - Planning (`smallHoursDefaultsToLateNight`: the add controls' starting
 *   answer for Late night / Early morning): late night when the day's last
 *   stop ended within 6 hours of the time; the choice stays visible.
 * - Either way, a date with no trip day hands the hours to the one that has
 *   one (a Day Out runs to 06:00 the next day — utils/dayOutHours).
 *
 * Minutes inside are in the day's own frame: a late-night stop's 01:30 is
 * 24 h + 90 min, so it sorts and compares after the evening.
 *
 * Pure — no React, no i18n; the clock comes in as an argument.
 */
import { stopLengthMinutes } from './quickStops';

export const LATE_NIGHT_CUTOFF = 6 * 60;
const DAY = 24 * 60;
const LIVE_LOOKAHEAD = 2 * 60;
const PLANNING_GAP = 6 * 60;
const DEFAULT_DURATION = 60;

const toMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(time || ''));
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

const pad = (n) => String(n).padStart(2, '0');

/** A Date as the local calendar day and minutes past midnight: { date: 'YYYY-MM-DD', minutes }. */
export function clockOf(d = new Date()) {
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, minutes: d.getHours() * 60 + d.getMinutes() };
}

function dayBefore(date) {
  const [y, m, d] = String(date).split('-').map(Number);
  const prev = new Date(Date.UTC(y, m - 1, d - 1));
  return `${prev.getUTCFullYear()}-${pad(prev.getUTCMonth() + 1)}-${pad(prev.getUTCDate())}`;
}

/** A stop filed under the evening at a small-hours time: the end of its day's night. */
export function isLateNightStop(activity) {
  const start = toMinutes(activity?.scheduledTime || activity?.time);
  const block = activity?.timeBlock || activity?.time_block;
  return start !== null && start < LATE_NIGHT_CUTOFF && block === 'evening';
}

/** A timed stop's { start, end } in its day's frame (late night after 24:00), or null when untimed. A quick stop ends where it starts. */
export function stopFrame(activity) {
  const clock = toMinutes(activity?.scheduledTime || activity?.time);
  if (clock === null) return null;
  const start = isLateNightStop(activity) ? clock + DAY : clock;
  return { start, end: start + stopLengthMinutes(activity, DEFAULT_DURATION) };
}

// Something of the new day's own under way or starting by `until`: a timed
// stop that isn't its night's tail, or a train/flight leaving that day.
function startsBy(day, transports, from, until) {
  const stopSoon = (day?.activities || []).some((a) => {
    if (!a || isLateNightStop(a)) return false;
    const f = stopFrame(a);
    return f && f.start <= until && f.end > from;
  });
  if (stopSoon) return true;
  return (transports || []).some((t) => {
    const dep = String(t?.departureLocal || '');
    if (dep.slice(0, 10) !== day.date) return false;
    const start = toMinutes(dep.slice(11, 16));
    if (start === null) return false;
    const arr = String(t.arrivalLocal || '');
    const end = arr.slice(0, 10) === day.date ? (toMinutes(arr.slice(11, 16)) ?? DAY) : DAY;
    return start <= until && Math.max(end, start + 1) > from;
  });
}

/**
 * The trip day the clock is on. `days`: one per date (its active plan),
 * { date, activities }; `transports`: the trip's segments (departureLocal /
 * arrivalLocal). → { date, minutes, lateNight } — `minutes` in that day's
 * frame (≥ 24 h on the night before), `date` the calendar day when neither
 * date is a trip day.
 */
export function tripNow(clock, days = [], transports = []) {
  const { date, minutes } = clock;
  const calendar = { date, minutes, lateNight: false };
  if (minutes >= LATE_NIGHT_CUTOFF) return calendar;
  const prevDate = dayBefore(date);
  const prevDay = (days || []).find(d => d?.date === prevDate);
  if (!prevDay) return calendar;
  const night = { date: prevDate, minutes: minutes + DAY, lateNight: true };
  const today = (days || []).find(d => d?.date === date);
  if (!today) return night;
  return startsBy(today, transports, minutes, minutes + LIVE_LOOKAHEAD) ? calendar : night;
}

/**
 * Planning: whether a small-hours `time` picked on `day` starts as Late night
 * (the end of that day's night) rather than Early morning. Late night when the
 * day's last stop — its night's tail included — ends within 6 hours before it.
 * `exclude`: the stop being moved, which mustn't anchor itself.
 */
export function smallHoursDefaultsToLateNight(day, time, { exclude = null } = {}) {
  const clock = toMinutes(time);
  if (clock === null || clock >= LATE_NIGHT_CUTOFF) return false;
  const at = clock + DAY;
  let lastEnd = null;
  (day?.activities || []).forEach((a) => {
    if (!a || a === exclude) return;
    const f = stopFrame(a);
    // An early-morning stop is the day's start, not its night.
    if (!f || f.start < LATE_NIGHT_CUTOFF) return;
    if (lastEnd === null || f.end > lastEnd) lastEnd = f.end;
  });
  return lastEnd !== null && lastEnd >= at - PLANNING_GAP;
}
